import { useTranslation } from '@app/hooks/use-translation';
import { EasyModal, Empty, SimpleAccordion } from '@ui';
import React, { FC, useEffect, useState } from 'react';
import {
  useGetMetricTemplateGroupsListQuery,
  useLazyGetSingleMetricTemplateQuery,
} from '@app/store/api/metric-template.api';
import {
  MetricTemplatesGroupType,
  MetricTemplatesType,
} from '@app/interfaces/slices-types/metric-templates.type';
import { toast } from 'react-toastify';

type MetricTemplatesModalPropsType = {
  onClose(): void;
  showModal: boolean;
  type: 'chat' | 'voice' | 'gpt';
  filterTypeTarget: string;
  fillMetricTrigger(metric: MetricTemplatesType): void;
};
const MetricTemplatesModal: FC<MetricTemplatesModalPropsType> = (props) => {
  const { onClose, showModal, type, filterTypeTarget, fillMetricTrigger } = props;
  const { t } = useTranslation('pages.metricTemplates');

  // api
  const { data: metricTemplates } = useGetMetricTemplateGroupsListQuery(type, {
    skip: !showModal,
  });
  const [getTemplate] = useLazyGetSingleMetricTemplateQuery();
  // api

  function onFillMetricFromTemplate(id: string) {
    getTemplate({ id })
      .unwrap()
      .then((metric) => fillMetricTrigger(metric))
      .finally(onClose);
  }
  useEffect(() => {
    if (!showModal) return;
    toast.warning(t('fill_from_template_warning'));
  }, [t, showModal]);
  // /?ACCORDION
  const [activeAccordionItem, setActiveAccordionItem] = useState<string | undefined>(undefined);

  const clickAccordionHeaderBtn = (id: string) => {
    if (id === activeAccordionItem) {
      if (id === '') {
        setActiveAccordionItem(undefined);
      } else {
        setActiveAccordionItem('');
      }
    } else {
      setActiveAccordionItem(id);
    }
  };

  // /?ACCORDION
  const filteredByTarget = metricTemplates?.reduce<MetricTemplatesGroupType[]>((acc, group) => {
    const matchedTemplates = group.metric_templates.filter(
      (template) => template.type === filterTypeTarget,
    );

    if (matchedTemplates.length > 0) {
      acc.push({
        ...group,
        metric_templates: matchedTemplates,
      });
    }

    return acc;
  }, []);

  return (
    <EasyModal
      show={showModal}
      onClose={onClose}
      label={<div className="">{t('from_template_add')}</div>}
      variant="mediumW700"
      withoutFooter
      withoutContentPadding
    >
      <div className="w-full px-[24px] py-4">
        {filteredByTarget && filteredByTarget?.length <= 0 ? (
          <Empty title={t('no_metric_templates')} />
        ) : (
          filteredByTarget?.map((group) => {
            return (
              <SimpleAccordion
                dataId={`ap-metric-group-${group.name}`}
                key={group.metric_templates_group_id}
                id={group.metric_templates_group_id}
                label={<div className="font-medium text-[15px] text-0color">{group.name}</div>}
                onChange={clickAccordionHeaderBtn}
                arrowClassName="!text-0color"
                opened={activeAccordionItem === group.metric_templates_group_id}
              >
                {activeAccordionItem === group.metric_templates_group_id && (
                  <ul className="flex w-full rounded-[12px] flex-col border-[1px] border-basic_app_bg">
                    {group.metric_templates?.map((type) => {
                      return (
                        <li
                          data-id={`ap-metric-group-${group.name}-${type.name}`}
                          className="w-full items-center justify-between flex border-b-[1px] border-basic_app_bg p-[10px] font-medium text-[15px] text-0color h-[64px]"
                          key={type.metric_template_id}
                        >
                          <button
                            onClick={() => onFillMetricFromTemplate(type.metric_template_id)}
                            className={`flex w-full items-center justify-between`}
                          >
                            {type.name}
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M5 12H19"
                                stroke="#1F2125"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M12 5L19 12L12 19"
                                stroke="#1F2125"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </SimpleAccordion>
            );
          })
        )}
      </div>
    </EasyModal>
  );
};

export default MetricTemplatesModal;
